import Avatar from 'core/modules/atoms/Avatar';
import { Icon } from 'ui';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { StudentCluster, StudentQuadrantData } from '../types';

import StudentClusterTooltip from './StudentClusterTooltip';
import './_quadrant-chart.scss';

type QuadrantChartProps = {
  clusters: StudentCluster[];
  loading: boolean;
  onStudentClick?: (student: StudentQuadrantData) => void;
};

const AVATAR_SIZE = 48;

function QuadrantChart({ clusters, loading, onStudentClick }: QuadrantChartProps): JSX.Element {
  const { t } = useTranslation(['chatbot']);
  const areaRef = useRef<HTMLDivElement>(null);

  const [selectedCluster, setSelectedCluster] = useState<{
    cluster: StudentCluster;
    position: { x: number; y: number };
  } | null>(null);

  const handleClusterClick = useCallback((cluster: StudentCluster, event: React.MouseEvent<HTMLDivElement>) => {
    const anchorEl = event.currentTarget;
    const rect = anchorEl.getBoundingClientRect();
    const parentRect = areaRef.current?.getBoundingClientRect();

    if (parentRect) {
      setSelectedCluster({
        cluster,
        position: {
          x: rect.left - parentRect.left + rect.width / 2,
          y: rect.top - parentRect.top,
        },
      });
    }
  }, []);

  const handleTooltipClose = useCallback(() => {
    setSelectedCluster(null);
  }, []);

  if (loading) {
    return <div className="quadrant-chart quadrant-chart--loading" />;
  }

  return (
    <div className="quadrant-chart">
      {/* Corner labels */}
      <div className="quadrant-chart__corner-label quadrant-chart__corner-label--top-left">{t('chatbot:ai_analytics_xray_label_bad_use')}</div>
      <div className="quadrant-chart__corner-label quadrant-chart__corner-label--top-right">{t('chatbot:ai_analytics_xray_label_motivated')}</div>
      <div className="quadrant-chart__corner-label quadrant-chart__corner-label--bottom-left">{t('chatbot:ai_analytics_xray_label_low_commitment')}</div>
      <div className="quadrant-chart__corner-label quadrant-chart__corner-label--bottom-right">{t('chatbot:ai_analytics_xray_label_efficient')}</div>
      {/* Axis lines */}
      <div className="quadrant-chart__axis-horizontal" />
      <div className="quadrant-chart__axis-vertical" />
      {/* Axis labels */}
      <div className="quadrant-chart__axis-label quadrant-chart__axis-label--x-negative">
        <Icon color="grey-03" size="md" name="minus-circle" />
        {t('chatbot:ai_analytics_xray_quality_axis')}
      </div>
      <div className="quadrant-chart__axis-label quadrant-chart__axis-label--x-positive">
        {t('chatbot:ai_analytics_xray_quality_axis')}
        <Icon color="grey-03" size="md" name="add-circle" />
      </div>
      <div className="quadrant-chart__axis-label quadrant-chart__axis-label--y-positive">
        <Icon color="grey-03" size="md" name="add-circle" />
        {t('chatbot:ai_analytics_xray_volume_axis')}
      </div>
      <div className="quadrant-chart__axis-label quadrant-chart__axis-label--y-negative">
        <Icon color="grey-03" size="md" name="minus-circle" />
        {t('chatbot:ai_analytics_xray_volume_axis')}
      </div>
      {/* Chart area with students */}
      <div className="quadrant-chart__area" ref={areaRef}>
        {clusters.map((cluster, index) => {
          const isSingle = cluster.students.length === 1;
          const student = cluster.students[0];

          const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
            if (isSingle && onStudentClick) {
              onStudentClick(student);
            } else {
              handleClusterClick(cluster, e);
            }
          };

          return (
            <div
              className={`quadrant-chart__cluster ${isSingle ? 'quadrant-chart__cluster--single' : 'quadrant-chart__cluster--multi'}`}
              key={`cluster-${index}`}
              onClick={handleClick}
              style={{
                left: cluster.x,
                top: cluster.y,
                width: AVATAR_SIZE,
                height: AVATAR_SIZE,
                transform: 'translate(-50%, -50%)',
              }}
            >
              {isSingle ? (
                <Avatar image={student.avatar || undefined} size="default" />
              ) : (
                <span className="quadrant-chart__cluster-count">+{cluster.students.length}</span>
              )}
            </div>
          );
        })}

        {selectedCluster && (
          <StudentClusterTooltip
            onClose={handleTooltipClose}
            onStudentClick={onStudentClick}
            position={selectedCluster.position}
            students={selectedCluster.cluster.students}
          />
        )}
      </div>
    </div>
  );
}

export default QuadrantChart;
